import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { 
  IndianRupee, TrendingUp, Users, Target, 
  Loader2, BarChart3, ChevronRight
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../utils/apiUrl';

const formatCurrency = (value) => {
  if (!value) return '₹0';
  return `₹${value.toLocaleString('en-IN')}`;
};

const IncentivesSummaryWidget = ({ compact = false }) => {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('monthly');
  const [data, setData] = useState(null);

  useEffect(() => {
    fetchSummary();
  }, [period, token]);

  const fetchSummary = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/incentives/summary?period=${period}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const result = await res.json();
        setData(result);
      }
    } catch (err) {
      console.error('Failed to fetch incentives summary:', err);
    } finally {
      setLoading(false);
    }
  };

  const periodLabels = {
    daily: 'Today',
    weekly: 'This Week',
    monthly: 'This Month',
    all: 'All Time'
  };

  if (loading) {
    return (
      <Card className="card-vault" data-testid="incentives-summary-widget">
        <CardContent className="p-6">
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-[#C5A059]" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="card-vault" data-testid="incentives-summary-widget">
      <CardHeader className={`border-b border-white/5 ${compact ? 'py-3' : ''}`}>
        <div className="flex items-center justify-between">
          <CardTitle className={`font-heading text-white flex items-center gap-2 ${compact ? 'text-base' : ''}`}>
            <BarChart3 className="w-5 h-5 text-[#C5A059]" />
            Incentives Overview
          </CardTitle>
          <div className="flex items-center gap-2">
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className={`${compact ? 'w-28' : 'w-32'} bg-slate-800 border-white/10 h-8 text-sm`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="last_trading_day">Last Trading Day</SelectItem>
                <SelectItem value="weekly">This Week</SelectItem>
                <SelectItem value="monthly">This Month</SelectItem>
                <SelectItem value="all">All Time</SelectItem>
              </SelectContent>
            </Select>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigate('/incentive-reports')}
              className="text-[#C5A059] hover:text-[#C5A059] hover:bg-[#C5A059]/10"
            >
              Reports <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className={compact ? 'p-3' : 'p-6'}>
        {/* Grand Total - Compact version */}
        <div className={`text-center ${compact ? 'mb-3 p-3' : 'mb-6 p-4'} bg-gradient-to-r from-[#C5A059]/20 to-[#C5A059]/5 rounded-xl border border-[#C5A059]/30`}>
          <p className={`${compact ? 'text-xs' : 'text-sm'} text-slate-400 mb-1`}>Total {periodLabels[period]} Incentives</p>
          <p className={`${compact ? 'text-2xl' : 'text-4xl'} font-bold text-[#C5A059]`}>
            {formatCurrency(data?.grand_total || 0)}
          </p>
        </div>

        {/* Breakdown Cards - More compact */}
        <div className={`grid grid-cols-3 gap-2 ${compact ? 'mb-3' : 'mb-6'}`}>
          <div className={`bg-emerald-500/10 rounded-lg ${compact ? 'p-2' : 'p-3'} border border-emerald-500/20 text-center`}>
            <Users className={`${compact ? 'w-4 h-4' : 'w-5 h-5'} text-emerald-400 mx-auto mb-1`} />
            <p className={`${compact ? 'text-[10px]' : 'text-xs'} text-emerald-300 mb-1`}>Regular Meetings</p>
            <p className={`${compact ? 'text-sm' : 'text-lg'} font-bold text-white`}>
              {formatCurrency(data?.breakdown?.regular_meetings || 0)}
            </p>
          </div>

          <div className={`bg-amber-500/10 rounded-lg ${compact ? 'p-2' : 'p-3'} border border-amber-500/20 text-center`}>
            <Target className={`${compact ? 'w-4 h-4' : 'w-5 h-5'} text-amber-400 mx-auto mb-1`} />
            <p className={`${compact ? 'text-[10px]' : 'text-xs'} text-amber-300 mb-1`}>Dormant Meetings</p>
            <p className={`${compact ? 'text-sm' : 'text-lg'} font-bold text-white`}>
              {formatCurrency(data?.breakdown?.dormant_meetings || 0)}
            </p>
          </div>

          <div className={`bg-[#C5A059]/10 rounded-lg ${compact ? 'p-2' : 'p-3'} border border-[#C5A059]/20 text-center`}>
            <TrendingUp className={`${compact ? 'w-4 h-4' : 'w-5 h-5'} text-[#C5A059] mx-auto mb-1`} />
            <p className={`${compact ? 'text-[10px]' : 'text-xs'} text-[#C5A059] mb-1`}>Activations</p>
            <p className={`${compact ? 'text-sm' : 'text-lg'} font-bold text-white`}>
              {formatCurrency(data?.breakdown?.dormant_activations || 0)}
            </p>
          </div>
        </div>

        {/* User Leaderboard - Show fewer rows in compact mode */}
        {data?.user_summaries?.length > 0 ? (
          <div>
            <h4 className={`${compact ? 'text-xs' : 'text-sm'} font-medium text-slate-300 mb-2`}>Staff Incentives</h4>
            <div className="border border-white/10 rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="border-b border-white/5 hover:bg-transparent">
                    <TableHead className={`text-slate-400 ${compact ? 'text-xs py-2' : ''}`}>#</TableHead>
                    <TableHead className={`text-slate-400 ${compact ? 'text-xs py-2' : ''}`}>Name</TableHead>
                    {!compact && <TableHead className="text-slate-400 text-right">Regular</TableHead>}
                    {!compact && <TableHead className="text-slate-400 text-right">Dormant</TableHead>}
                    {!compact && <TableHead className="text-slate-400 text-right">Activations</TableHead>}
                    <TableHead className={`text-slate-400 text-right ${compact ? 'text-xs py-2' : ''}`}>Total</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.user_summaries.slice(0, compact ? 5 : 10).map((user, idx) => (
                    <TableRow key={user.user_id} className={`border-b border-white/5 hover:bg-white/5 ${compact ? 'text-xs' : ''}`}>
                      <TableCell className={`text-slate-500 ${compact ? 'py-1.5' : ''}`}>{idx + 1}</TableCell>
                      <TableCell className={`text-white font-medium ${compact ? 'py-1.5' : ''}`}>{user.user_name}</TableCell>
                      {!compact && (
                        <TableCell className="text-right text-emerald-400">
                          {user.regular_meetings.count > 0 ? (
                            <span>{formatCurrency(user.regular_meetings.amount)} <span className="text-xs text-slate-500">({user.regular_meetings.count})</span></span>
                          ) : '-'}
                        </TableCell>
                      )}
                      {!compact && (
                        <TableCell className="text-right text-amber-400">
                          {user.dormant_meetings.count > 0 ? (
                            <span>{formatCurrency(user.dormant_meetings.amount)} <span className="text-xs text-slate-500">({user.dormant_meetings.count})</span></span>
                          ) : '-'}
                        </TableCell>
                      )}
                      {!compact && (
                        <TableCell className="text-right text-[#C5A059]">
                          {user.dormant_activations.count > 0 ? (
                            <span>{formatCurrency(user.dormant_activations.amount)} <span className="text-xs text-slate-500">({user.dormant_activations.count})</span></span>
                          ) : '-'}
                        </TableCell>
                      )}
                      <TableCell className={`text-right text-white font-bold ${compact ? 'py-1.5' : ''}`}>
                        {formatCurrency(user.total_incentive)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {data.user_summaries.length > (compact ? 5 : 10) && (
              <p className="text-xs text-slate-500 text-center mt-2">
                Showing top {compact ? 5 : 10} of {data.user_summaries.length} staff members
              </p>
            )}
          </div>
        ) : (
          <div className={`text-center ${compact ? 'py-2' : 'py-4'} text-slate-500`}>
            <BarChart3 className={`${compact ? 'w-6 h-6' : 'w-8 h-8'} mx-auto mb-2 opacity-50`} />
            <p className="text-sm">No incentives recorded {periodLabels[period].toLowerCase()}</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default IncentivesSummaryWidget;
